export type FilterGate = "and" | "or"

export type Filter = {
  field: string
  operator: string
  value: string
  by: string
  values: string[]
  gate?: FilterGate
  groupId?: number
}

function sameValues(a: string[] = [], b: string[] = []): boolean {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((v) => set.has(v))
}

// Pills keep their selection in `values` (multi-select) or `value` (free text), so both count.
export function filtersEqual(a: Partial<Filter>[] = [], b: Partial<Filter>[] = []): boolean {
  if (a.length !== b.length) return false
  return a.every((fa, i) => {
    const fb = b[i]
    return (
      !!fb &&
      fa.field === fb.field &&
      fa.operator === fb.operator &&
      fa.by === fb.by &&
      (fa.value ?? "") === (fb.value ?? "") &&
      sameValues(fa.values, fb.values) &&
      (fa.gate ?? "and") === (fb.gate ?? "and") &&
      fa.groupId === fb.groupId
    )
  })
}

export function splitFiltersIntoOrGroups<T extends Pick<Filter, "gate">>(filters: T[]): T[][] {
  const groups: T[][] = []
  for (const filter of filters) {
    const lastGroup = groups[groups.length - 1]
    if (!lastGroup || filter.gate === "or") {
      groups.push([filter])
    } else {
      lastGroup.push(filter)
    }
  }
  return groups
}

type GroupableFilter = Pick<Filter, "gate" | "groupId">

export type FilterGroupUnit<T extends GroupableFilter> = {
  groupId?: number
  gate: FilterGate
  filters: T[]
}

export function splitFiltersIntoGroupUnits<T extends GroupableFilter>(
  filters: T[],
): FilterGroupUnit<T>[] {
  const units: FilterGroupUnit<T>[] = []
  for (const filter of filters) {
    const last = units[units.length - 1]
    if (last && filter.groupId != null && last.groupId === filter.groupId) {
      last.filters.push(filter)
    } else {
      units.push({ groupId: filter.groupId, gate: filter.gate ?? "and", filters: [filter] })
    }
  }
  return units
}
